import React, { useEffect, useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { FrozenColumnScroll } from '../layout/FrozenColumnScroll';
import { numberPadInputProps } from '../layout/keyboard';
import { GlassButton } from '../ui/GlassButton';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from '../charts/d3/format';
import type { AmortizationRow, ExtraPayment } from '../../utils/loanAmortization';
import { formatPaymentPeriod } from '../../utils/loanAmortization';

type AmortizationTableProps = {
  rows: AmortizationRow[];
  extras: ExtraPayment[];
  recurringExtra: number;
  startMonth?: string | null;
  /** 1-based payment month for "now", or null when no start date. */
  currentMonth?: number | null;
  onRecurringExtraChange: (value: number) => void;
  onSetExtraForMonth: (month: number, amount: number) => void;
  onAddExtraPayment: (month: number, amount: number) => void;
  /** Max rows before "Show more". */
  initialVisible?: number;
};

const COLUMNS = [
  { key: 'period', label: 'Period', width: 88 },
  { key: 'payment', label: 'Payment', width: 76 },
  { key: 'principal', label: 'Principal', width: 76 },
  { key: 'interest', label: 'Interest', width: 76 },
  { key: 'extra', label: 'Extra', width: 88 },
  { key: 'balance', label: 'Balance', width: 84 },
] as const;

function extrasMap(extras: ExtraPayment[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const e of extras) map.set(e.month, e.amount);
  return map;
}

function ExtraCell({
  periodLabel,
  oneTimeAmount,
  recurringExtra,
  appliedExtra,
  onCommit,
}: {
  periodLabel: string;
  oneTimeAmount: number;
  recurringExtra: number;
  /** Actual extra applied this month (recurring + one-time, capped by balance). */
  appliedExtra: number;
  onCommit: (oneTimeAmount: number) => void;
}) {
  const displayTotal = Math.round(oneTimeAmount + recurringExtra);
  const [text, setText] = useState(
    appliedExtra > 0 ? String(Math.round(appliedExtra)) : '',
  );
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setText(appliedExtra > 0 ? String(Math.round(appliedExtra)) : '');
    }
  }, [appliedExtra, focused]);

  const commit = () => {
    const cleaned = text.replace(/[$,\s]/g, '').trim();
    if (cleaned === '') {
      // Clear one-time only; monthly add-on still applies via recurringExtra
      onCommit(0);
      setText(recurringExtra > 0 ? String(Math.round(recurringExtra)) : '');
      return;
    }
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setText(appliedExtra > 0 ? String(Math.round(appliedExtra)) : '');
      return;
    }
    const total = Math.round(parsed);
    // Cell edits total extra for the month; strip recurring to store one-time only
    const nextOneTime = Math.max(0, total - Math.round(recurringExtra));
    onCommit(nextOneTime);
    const shown = nextOneTime + Math.round(recurringExtra);
    setText(shown > 0 ? String(shown) : '');
  };

  return (
    <View
      style={[
        styles.extraInputWrap,
        focused && styles.extraInputFocused,
        appliedExtra > 0 && styles.extraInputActive,
      ]}
    >
      <Text style={styles.extraPrefix}>$</Text>
      <TextInput
        style={styles.extraInput}
        value={text}
        placeholder={recurringExtra > 0 ? String(Math.round(recurringExtra)) : '0'}
        placeholderTextColor={colors.textMuted}
        {...numberPadInputProps()}
        selectTextOnFocus
        accessibilityLabel={`Extra payment for ${periodLabel}`}
        onFocus={() => {
          setFocused(true);
          setText(displayTotal > 0 ? String(displayTotal) : '');
        }}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onSubmitEditing={commit}
        onChangeText={(next) => {
          if (!/^[0-9]*$/.test(next)) return;
          setText(next);
        }}
      />
    </View>
  );
}

export function AmortizationTable({
  rows,
  extras,
  recurringExtra,
  startMonth = null,
  currentMonth = null,
  onRecurringExtraChange,
  onSetExtraForMonth,
  onAddExtraPayment,
  initialVisible = 24,
}: AmortizationTableProps) {
  const [expanded, setExpanded] = useState(false);
  const [fromNow, setFromNow] = useState(true);
  const oneTime = useMemo(() => extrasMap(extras), [extras]);
  const periodLabel = (month: number) => formatPaymentPeriod(month, startMonth);
  const anchorMonth =
    currentMonth != null && currentMonth > 0 ? Math.min(currentMonth, rows.length) : null;
  const showNowToggle = anchorMonth != null && anchorMonth > 1;

  const quickChips = useMemo(() => {
    const base = anchorMonth ?? 1;
    return [
      { label: '+$500 this month', month: base, amount: 500 },
      { label: '+$1k in 1 year', month: base + 12, amount: 1000 },
      { label: '+$5k in 5 years', month: base + 60, amount: 5000 },
    ];
  }, [anchorMonth]);

  const displayRows = useMemo(() => {
    if (expanded) return rows;
    if (fromNow && anchorMonth != null) {
      const from = Math.max(0, anchorMonth - 1);
      return rows.slice(from, from + initialVisible);
    }
    return rows.slice(0, initialVisible);
  }, [rows, expanded, fromNow, anchorMonth, initialVisible]);
  const hasMore = rows.length > displayRows.length || expanded;

  if (rows.length === 0) {
    return (
      <Text style={styles.empty}>Enter a loan amount to see the amortization schedule.</Text>
    );
  }

  return (
    <View style={styles.wrap}>
      <Text style={styles.hint}>
        “Every month” fills the Extra column on each row. Tap a cell to add more for that month only.
      </Text>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.chipRow}
      >
        {quickChips.map((chip) => (
          <GlassButton
            key={chip.label}
            variant="secondary"
            size="sm"
            onPress={() => onAddExtraPayment(chip.month, chip.amount)}
          >
            {chip.label}
          </GlassButton>
        ))}
      </ScrollView>

      <View style={styles.recurringRow}>
        <View style={styles.recurringCopy}>
          <Text style={styles.recurringLabel}>Every month</Text>
          <Text style={styles.recurringHint}>
            Shows in Extra on every row and lowers the balance
          </Text>
        </View>
        <View style={styles.recurringControls}>
          <GlassButton
            variant="secondary"
            size="icon"
            accessibilityLabel="Decrease monthly extra"
            onPress={() => onRecurringExtraChange(Math.max(0, recurringExtra - 100))}
          >
            −
          </GlassButton>
          <View style={styles.recurringValue}>
            <Text style={styles.recurringAmount}>
              {recurringExtra > 0 ? formatCurrency(recurringExtra) : '$0'}
            </Text>
          </View>
          <GlassButton
            variant="secondary"
            size="icon"
            accessibilityLabel="Increase monthly extra"
            onPress={() => onRecurringExtraChange(recurringExtra + 100)}
          >
            +
          </GlassButton>
        </View>
      </View>

      {showNowToggle && (
        <View style={styles.viewRow}>
          <GlassButton
            variant="secondary"
            size="sm"
            selected={fromNow && !expanded}
            onPress={() => {
              setExpanded(false);
              setFromNow(true);
            }}
          >
            This month
          </GlassButton>
          <GlassButton
            variant="secondary"
            size="sm"
            selected={!fromNow && !expanded}
            onPress={() => {
              setExpanded(false);
              setFromNow(false);
            }}
          >
            From start
          </GlassButton>
        </View>
      )}
      {extras.length > 0 && (
        <View style={styles.activeExtras}>
          {extras.map((extra) => (
            <Pressable
              key={extra.month}
              style={styles.activeChip}
              onPress={() => onSetExtraForMonth(extra.month, 0)}
              accessibilityRole="button"
              accessibilityLabel={`Remove extra on ${periodLabel(extra.month)}`}
            >
              <Text style={styles.activeChipText}>
                {periodLabel(extra.month)} · {formatCurrency(extra.amount)} ×
              </Text>
            </Pressable>
          ))}
        </View>
      )}

      <FrozenColumnScroll
        frozenWidth={88}
        frozen={
          <View>
            <View style={styles.headerRow}>
              <View style={[styles.cell, styles.headerCell, { width: 88 }]}>
                <Text style={styles.headerText} numberOfLines={1}>
                  Period
                </Text>
              </View>
            </View>
            {displayRows.map((row) => {
              const hasExtra = row.extra > 0;
              const isCurrent = anchorMonth === row.month;
              const label = periodLabel(row.month);
              return (
                <View
                  key={row.month}
                  style={[
                    styles.dataRow,
                    hasExtra && styles.extraRow,
                    isCurrent && styles.currentRow,
                  ]}
                >
                  <View style={[styles.cell, styles.monthCell, { width: 88 }]}>
                    <Text style={[styles.monthText, isCurrent && styles.currentMonthText]} numberOfLines={1}>
                      {label}
                    </Text>
                    {isCurrent ? <Text style={styles.nowBadge}>Now</Text> : null}
                  </View>
                </View>
              );
            })}
          </View>
        }
        scrollable={
          <View>
            <View style={styles.headerRow}>
              {COLUMNS.filter((col) => col.key !== 'period').map((col) => (
                <View
                  key={col.key}
                  style={[
                    styles.cell,
                    styles.headerCell,
                    { width: col.width },
                    col.key === 'balance' && styles.balanceHeader,
                    col.key === 'extra' && styles.extraHeader,
                  ]}
                >
                  <Text
                    style={[
                      styles.headerText,
                      col.key === 'balance' && styles.balanceHeaderText,
                      col.key === 'extra' && styles.extraHeaderText,
                    ]}
                    numberOfLines={1}
                  >
                    {col.label}
                  </Text>
                </View>
              ))}
            </View>
            {displayRows.map((row) => {
              const oneTimeAmount = oneTime.get(row.month) ?? 0;
              const hasExtra = row.extra > 0;
              const isCurrent = anchorMonth === row.month;
              const label = periodLabel(row.month);
              return (
                <View
                  key={row.month}
                  style={[
                    styles.dataRow,
                    hasExtra && styles.extraRow,
                    isCurrent && styles.currentRow,
                  ]}
                >
                  <View style={[styles.cell, { width: 76 }]}>
                    <Text style={styles.cellText} numberOfLines={1}>
                      {formatCurrency(row.payment)}
                    </Text>
                  </View>
                  <View style={[styles.cell, { width: 76 }]}>
                    <Text style={styles.cellText} numberOfLines={1}>
                      {formatCurrency(row.principal)}
                    </Text>
                  </View>
                  <View style={[styles.cell, { width: 76 }]}>
                    <Text style={[styles.cellText, styles.interestText]} numberOfLines={1}>
                      {formatCurrency(row.interest)}
                    </Text>
                  </View>
                  <View style={[styles.cell, styles.extraCell, { width: 88 }]}>
                    <ExtraCell
                      periodLabel={label}
                      oneTimeAmount={oneTimeAmount}
                      recurringExtra={recurringExtra}
                      appliedExtra={row.extra}
                      onCommit={(amount) => onSetExtraForMonth(row.month, amount)}
                    />
                  </View>
                  <View style={[styles.cell, styles.balanceCell, { width: 84 }]}>
                    <Text style={[styles.cellText, styles.balanceText]} numberOfLines={1}>
                      {formatCurrency(row.balance)}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        }
      />
      {hasMore && (
        <GlassButton variant="secondary" size="sm" onPress={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show all ${rows.length} months`}
        </GlassButton>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  empty: {
    ...typography.caption,
    color: colors.textMuted,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  chipRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  recurringRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: 'rgba(61, 214, 164, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.18)',
  },
  recurringCopy: {
    flex: 1,
    gap: 2,
  },
  recurringLabel: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  recurringHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  recurringControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  recurringValue: {
    minWidth: 64,
    alignItems: 'center',
  },
  recurringAmount: {
    ...typography.headline,
    fontSize: 16,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  activeExtras: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  activeChip: {
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.28)',
  },
  activeChipText: {
    ...typography.caption,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.surfaceElevated,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 52,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  extraRow: {
    backgroundColor: 'rgba(61, 214, 164, 0.06)',
  },
  currentRow: {
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  viewRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  cell: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    justifyContent: 'center',
  },
  headerCell: {
    paddingVertical: spacing.sm,
  },
  monthCell: {
    backgroundColor: colors.surface,
    height: 52,
    justifyContent: 'center',
  },
  extraCell: {
    paddingVertical: 4,
  },
  balanceCell: {
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  balanceHeader: {
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
  },
  extraHeader: {
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  headerText: {
    ...typography.label,
    color: colors.textMuted,
    fontSize: 10,
  },
  balanceHeaderText: {
    color: colors.accent,
  },
  extraHeaderText: {
    color: colors.accent,
  },
  cellText: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
    fontSize: 12,
  },
  monthText: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
    fontSize: 11,
    fontVariant: ['tabular-nums'],
  },
  currentMonthText: {
    color: colors.accent,
  },
  nowBadge: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
    fontSize: 9,
    marginTop: 2,
  },
  interestText: {
    color: colors.warning,
  },
  balanceText: {
    color: colors.text,
    fontWeight: '600',
  },
  extraInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 36,
    paddingHorizontal: 6,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceElevated,
  },
  extraInputFocused: {
    borderColor: colors.accent,
  },
  extraInputActive: {
    borderColor: 'rgba(61, 214, 164, 0.35)',
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  extraPrefix: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  extraInput: {
    flex: 1,
    ...typography.caption,
    color: colors.accent,
    fontWeight: '600',
    fontSize: 12,
    paddingVertical: 6,
    paddingHorizontal: 2,
    fontVariant: ['tabular-nums'],
  },
});
